import { useCallback, useEffect, useRef, useState } from "react";
import type { UseFormSetValue, UseFormWatch } from "react-hook-form";
import { toast } from "react-hot-toast";
import {
  useGoogleAutocomplete,
  useISOData,
  type GooglePrediction,
} from "@tagadapay/plugin-sdk/react";
import type { PluginConfig } from "@/src/types/plugin";
import type { CheckoutFormData } from "@/src/lib/schemas/checkout-schemas";

interface UseAddressAutocompleteProps {
  pluginConfig: PluginConfig;
  setValue: UseFormSetValue<CheckoutFormData>;
  watch: UseFormWatch<CheckoutFormData>;
}

export function useAddressAutocomplete({
  pluginConfig,
  setValue,
  watch,
}: UseAddressAutocompleteProps) {
  const {
    predictions,
    searchPlaces,
    getPlaceDetails,
    extractAddressComponents,
    clearPredictions,
  } = useGoogleAutocomplete({
    apiKey: pluginConfig.googleApiKey,
    language: "en",
  });

  const { countries, getRegions, mapGoogleToISO } = useISOData();

  const [addressInput, setAddressInput] = useState("");
  const [showPredictions, setShowPredictions] = useState(false);
  const [selectedCountry, setSelectedCountry] = useState("");
  const [availableStates, setAvailableStates] = useState<
    Array<{ code: string; name: string }>
  >([]);
  const [isCountrySelected, setIsCountrySelected] = useState(false);

  const watchedCountry = watch("country");
  const prevCountryRef = useRef<string | undefined>(undefined);

  // Keep a ref to the watch function to avoid dependency issues
  const watchRef = useRef(watch);
  watchRef.current = watch;

  useEffect(() => {
    // Only process if country actually changed to a different value
    if (watchedCountry && watchedCountry !== prevCountryRef.current) {
      setSelectedCountry(watchedCountry);
      setIsCountrySelected(true);

      const states = getRegions(watchedCountry);
      const mappedStates = states.map((state: any) => ({
        code: state.iso,
        name: state.name,
      }));
      setAvailableStates(mappedStates || []);

      const currentState = watchRef.current("state");
      if (
        currentState &&
        !mappedStates?.find((s: any) => s.code === currentState)
      ) {
        setValue("state", "");
      }

      // Only clear address fields if there was a previous country (user changed country)
      // Don't clear on initial load or when same country is set again
      if (prevCountryRef.current !== undefined) {
        setValue("address1", "");
        setValue("city", "");
        setValue("postal", "");
        setAddressInput("");
        setShowPredictions(false);
      }

      prevCountryRef.current = watchedCountry;
    } else if (!watchedCountry) {
      setIsCountrySelected(false);
      setSelectedCountry("");
      setAvailableStates([]);
      prevCountryRef.current = undefined;
    }
  }, [watchedCountry, getRegions, setValue]);

  const handleAddressInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      try {
        console.log("isCountrySelected", isCountrySelected);
        if (!isCountrySelected) {
          return;
        }

        console.log("selectedCountry", selectedCountry);

        const value = e.target.value;
        setAddressInput(value);

        console.log("set value address1", value);
        setValue("address1", value);

        if (value.length >= 3 && selectedCountry) {
          searchPlaces(value, selectedCountry);
          setShowPredictions(true);
        } else {
          setShowPredictions(false);
          clearPredictions();
        }
      } catch (error) {
        console.error("error", error);
      }
    },
    [
      searchPlaces,
      setValue,
      selectedCountry,
      isCountrySelected,
      clearPredictions,
    ],
  );

  const handlePredictionSelect = useCallback(
    async (prediction: GooglePrediction) => {
      // Set input immediately with the prediction description to provide visual feedback

      console.log("prediction", prediction);
      setAddressInput(prediction.description);
      setShowPredictions(false);
      clearPredictions();

      try {
        const placeDetails = await getPlaceDetails(prediction.place_id);

        if (placeDetails) {
          const extracted = extractAddressComponents(placeDetails);

          const address1 =
            `${extracted.streetNumber} ${extracted.route}`.trim();

          // Update both the form value and local input state with the extracted address
          setValue("address1", address1, {
            shouldValidate: true,
            shouldDirty: true,
          });
          setAddressInput(address1);

          setValue("city", extracted.locality, {
            shouldValidate: true,
            shouldDirty: true,
          });
          setValue("postal", extracted.postalCode, {
            shouldValidate: true,
            shouldDirty: true,
          });

          const mappedState = mapGoogleToISO(
            extracted.administrativeAreaLevel1,
            extracted.administrativeAreaLevel1Long,
            selectedCountry,
          );

          if (mappedState) {
            setValue("state", mappedState.iso, {
              shouldValidate: true,
              shouldDirty: true,
            });
          } else {
            setValue("state", extracted.administrativeAreaLevel1, {
              shouldValidate: true,
              shouldDirty: true,
            });
          }

          // Don't re-set the country - it's already selected and doing so can trigger
          // the useEffect that clears address fields
        }
      } catch (error) {
        console.error("Error fetching place details:", error);
        // Keep the prediction description as fallback
        setValue("address1", prediction.description, {
          shouldValidate: true,
          shouldDirty: true,
        });
      }
    },
    [
      getPlaceDetails,
      extractAddressComponents,
      setValue,
      selectedCountry,
      mapGoogleToISO,
      clearPredictions,
    ],
  );

  const handleCountryChange = useCallback(
    (e: React.ChangeEvent<HTMLSelectElement>) => {
      const countryCode = e.target.value;
      setValue("country", countryCode);

      if (countryCode && !isCountrySelected) {
        toast.success("Great! Now you can enter your address details below.");
      }
    },
    [setValue, isCountrySelected],
  );

  return {
    countries,
    availableStates,
    isCountrySelected,
    addressInput,
    showPredictions,
    predictions,
    handleAddressInputChange,
    handlePredictionSelect,
    handleCountryChange,
  };
}
